import RequiredIndicator from "@base/ui/RequiredIndicator";
import { Selector } from "@base/ui/Selector";
import { createListCollection } from "@ark-ui/react/select";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { EligiblePlan } from "@features/membership/model/types";
import { formatPlanDisplay } from "@features/membership/lib/utils";

export interface EligiblePlanSelectorProps {
  items: EligiblePlan[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

interface PlanOption {
  value: string;
  label: string;
}

const EligiblePlanSelector = ({
  items,
  value,
  onValueChange,
  placeholder,
  label,
  required = false,
  disabled = false,
}: EligiblePlanSelectorProps) => {
  const { t } = useTranslation();

  const options: PlanOption[] = useMemo(
    () =>
      items.map((plan) => ({
        value: plan.type,
        label: formatPlanDisplay(plan.type, plan.billing_cycle, t),
      })),
    [items, t],
  );

  const collection = useMemo(
    () =>
      createListCollection<PlanOption>({
        items: options,
      }),
    [options],
  );

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <span className="text-gray-900 text-label2">
          {label} {required && <RequiredIndicator />}
        </span>
      )}
      <Selector<PlanOption>
        collection={collection}
        value={value ? [value] : []}
        onValueChange={(details) => {
          const selectedValue = details.value[0];
          if (selectedValue) {
            onValueChange(selectedValue);
          }
        }}
        disabled={disabled}
        className="w-full"
      >
        <Selector.Control size="medium" variant="outline">
          {({ selectedItems }) => {
            const selected = selectedItems[0] as PlanOption | undefined;
            return <span className="truncate">{selected?.label ?? placeholder ?? "Select plan"}</span>;
          }}
        </Selector.Control>
        <Selector.Content className="overflow-y-auto z-[10003] p-2 max-h-60">
          {collection.items.map((item) => (
            <Selector.Item key={item.value} item={item} className="px-3 py-2 cursor-pointer hover:bg-gray-50">
              {({ selected }) => (
                <div className="flex gap-2 justify-between items-center">
                  <span className="text-label2 text-gray-1000">{item.label}</span>
                  {selected && <span className="text-primary-500">✓</span>}
                </div>
              )}
            </Selector.Item>
          ))}
        </Selector.Content>
      </Selector>
    </div>
  );
};

export default EligiblePlanSelector;
